import { useState } from 'react';
import { get, post, remove as rm } from '../../redux/api';
import { useAsyncStatus } from '../index';
import { makeRequest, Method } from './makeRequest';
import useRequestEventEmitter from './useRequestEventEmitter';
import type { AsyncStatus } from '../../types';
import type { CreateUserInvitationsRequest, GetUserInvitationsResponse, UserInvitation } from '../../../typings/api';
import type { DeleteRequestEventContext, GetRequestEventContext, PutRequestEventContext, RequestEventEmitter } from './makeRequest';

export type UserInvitationsStore = {
  data: UserInvitation[];
  fetchAll(): Promise<void>;
  fetchStatus: AsyncStatus;
  create(emails: string[]): Promise<void>;
  createStatus: AsyncStatus;
  remove(email: string): Promise<void>;
  removeStatus: AsyncStatus;
  eventEmitter: RequestEventEmitter<UserInvitationsRequestContext>;
};

export type UserInvitationsRequestContext =
  | GetRequestEventContext<GetUserInvitationsResponse>
  | PutRequestEventContext<UserInvitation[]>
  | DeleteRequestEventContext;

/**
 * A store of UserInvitations.
 *
 * This hook does not have any mount or un-mount effects.
 * @deprecated - This pattern is outdated -- use useAsyncValue and friends (like useAsyncRequest) instead.
 */
const useUserInvitations = (organizationId: number): UserInvitationsStore => {
  const [data, setData] = useState<UserInvitation[]>([]);
  const { status: fetchStatus, ...fetchStatusFns } = useAsyncStatus();
  const { status: createStatus, ...createStatusFns } = useAsyncStatus();
  const { status: removeStatus, ...removeStatusFns } = useAsyncStatus();
  const eventEmitter = useRequestEventEmitter<UserInvitationsRequestContext>();

  const fetchAll = () =>
    makeRequest<GetUserInvitationsResponse, UserInvitationsRequestContext>({
      req: () => get<GetUserInvitationsResponse>('/api/user-invitations'),
      setData: responseData => setData(responseData || []),
      getEventContext: responseData => ({ method: Method.GET, data: responseData }),
      statusFns: fetchStatusFns,
      eventEmitter,
    });

  const create = async (emails: string[]) =>
    makeRequest<UserInvitation[], UserInvitationsRequestContext>({
      req: () => post<CreateUserInvitationsRequest, UserInvitation[]>(`/api/organizations/${organizationId}/invite`, { emails }),
      setData: responseData => setData(currentData => [...currentData, ...(responseData || [])]),
      getEventContext: responseData => ({ method: Method.PUT, data: responseData }),
      statusFns: createStatusFns,
      eventEmitter,
    });

  const remove = async (email: string) =>
    makeRequest<undefined, UserInvitationsRequestContext>({
      req: () => rm<undefined>(`/api/user-invitations/${email}`),
      setData: () => setData(currentData => currentData.filter(inv => inv.email !== email)),
      getEventContext: () => ({ method: Method.DELETE }),
      statusFns: removeStatusFns,
      eventEmitter,
    });

  return {
    data,
    fetchAll,
    fetchStatus,
    create,
    createStatus,
    remove,
    removeStatus,
    eventEmitter,
  };
};

export default useUserInvitations;
